import { getValFromObject } from "../config/FeatureDashboardVarMap";

import axios from '../api/axios';
import { updateCampaignRecentColoursRoute } from "../api/routes";

import hexRgb from 'hex-rgb';

export const HEX_TO_RGBA = (colour, opacity = 1) => {
    // console.log('HEX_TO_RGBA: ', colour, opacity, '=>', hexRgb(colour, {format:'css', alpha:opacity}));
    return hexRgb(colour, {format:'css', alpha:opacity})
}

export const DEFAULT_COLOURS = [
    '#9146FF',
    '#1CC8EE',
    '#14142B',
];

export const COLOUR_INDEXES = {
    primary: 0,
    secondary: 1,
    tertiary: 2,
    // Other colours will be called colour4, colour5, etc. Or simply the colour's hex value.
}

// A bunch of search strings we can replace the the proper strings + some shorthand versions
export const ALT_NAMES = [
    {search: 'colour_', replace: 'color'},
    {search: 'colour', replace: 'color'},
    {search: 'color_', replace: 'color'},
    {search: 'first', replace: 'primary'},
    {search: 'second', replace: 'secondary'},
    {search: 'third', replace: 'tertiary'},
    {search: 'fourth', replace: 'color4'},
    {search: 'fifth', replace: 'color5'},
    {search: 'sixth', replace: 'color6'},
    {search: 'seventh', replace: 'color7'},
    {search: 'eighth', replace: 'color8'},
    {search: 'ninth', replace: 'color9'},
    {search: 'tenth', replace: 'color10'},
    {search: '1st', replace: 'primary'},
    {search: '2nd', replace: 'secondary'},
    {search: '3rd', replace: 'tertiary'},
    {search: '4th', replace: 'color4'},
    {search: '5th', replace: 'color5'},
    {search: '6th', replace: 'color6'},
    {search: '7th', replace: 'color7'},
    {search: '8th', replace: 'color8'},
    {search: '9th', replace: 'color9'},
    {search: '10th', replace: 'color10'},
    {search: '11th', replace: 'color11'},
    {search: '12th', replace: 'color12'},
    {search: '13th', replace: 'color13'},
    {search: '14th', replace: 'color14'},
    {search: '15th', replace: 'color15'},
    {search: 'pri', replace: 'primary'},
    {search: 'sec', replace: 'secondary'},
    {search: 'ter', replace: 'tertiary'},
    {search: 'col1', replace: 'primary'},
    {search: 'col2', replace: 'secondary'},
    {search: 'col3', replace: 'tertiary'},
    {search: 'col4', replace: 'color4'},
    {search: 'col5', replace: 'color5'},
    {search: 'col6', replace: 'color6'},
    {search: 'col7', replace: 'color7'},
    {search: 'col8', replace: 'color8'},
    {search: 'col9', replace: 'color9'},
];
const replaceAltNames = (str) => {
    if (typeof str !== 'string') {
        return str;
    }
    if (str) {
        str = str.toLowerCase();
        ALT_NAMES.forEach((altName) => {
            if (str.includes(altName.search) && str !== altName.replace) {
                str = str.replace(altName.search, altName.replace);
            }
        });
    }
    return str.toUpperCase();
}

// Accepts a colour name (primary, secondary, tertiary) or a hex value (eg. #FF0000)
// Doesn't care about the case of the colour name
// Returns the hex value of the colour or default twitch purple if invalid
export const COLOUR_NAME_TO_COLOUR_HEX = (colour, campaignData) => {
    if (typeof colour !== 'string') {
        return colour;
    }
    const campaignColours = campaignData?.colours;
    colour = replaceAltNames(colour);
    // console.log('GET_COLOUR: ', colour, campaignData, campaignColours);
    if (Array.isArray(campaignColours)) {
        if (COLOUR_INDEXES[colour?.toLowerCase()] !== undefined) {
            return campaignColours[COLOUR_INDEXES[colour?.toLowerCase()]];
        }
        if (colour?.toLowerCase().startsWith('color') && !isNaN(colour.slice(5))) {
            const index = parseInt(colour.slice(5));
            if (index >= 1 && index <= campaignData?.colours.length) {
                return campaignColours[index - 1];
            }
        }
    }
    if (colour?.substring(0, 1) !== '#') {
        colour = '#' + colour;
    }
    if (/^#[0-9A-F]{6}$/i.test(colour)) {
        return colour;
    }
    return DEFAULT_COLOURS[0];
}

// Used by the colour picker to get the colour name from the colour value, meaning if the user enters a hex value that matches a colour name, 
// we can return the colour name and store that instead of the hex value.
// Accepts a colour name (primary, secondary, tertiary) or a hex value (eg. #FF0000)
// Doesn't care about the case of the colour name
// Returns the colour name (primary, secondary, tertiary) or a colour string (eg. color4, color5, etc.)
// Note colour names are always in returned uppercase, so in theory what the end user sees is always upper case,
// EXCEPT if they type it in themselves, in which case we allow them to type it in any case they like.
// Returns undefined if the colour is not a valid campaign colour
export const COLOUR_HEX_TO_COLOUR_NAME = (colour, campaignData) => {
    if (typeof colour !== 'string') {
        return colour;
    }
    if (COLOUR_INDEXES[colour?.toLowerCase()] !== undefined) {
        return colour;
    }
    if (colour?.toLowerCase().startsWith('color') && !isNaN(colour.slice(5))) {
        const index = parseInt(colour.slice(5));
        if (index >= 1 && index <= 3) {
            return (Object.keys(COLOUR_INDEXES)[index - 1]).toUpperCase();
        }
        if (index >= 4 && index <= campaignData?.colours.length) {
            return colour.toUpperCase();
        }
    }
    // We get here if the colour is not a valid campaign colour string (ie. in theory it is a hex colour, but it could be invalid)
    // See if it matches any of the colours in campaignData.colours array
    // Add a # to the start of the colour if it doesn't already have one
    if (colour.substring(0, 1) !== '#') {
        colour = '#' + colour;
    }
    if (/^#[0-9A-F]{6}$/i.test(colour) && colour.length === 7) {
        const campaignColours = campaignData?.colours;
        if (Array.isArray(campaignColours)) {
            for (let i = 0; i < campaignColours.length; i++) {
                if (campaignColours[i].toLowerCase() === colour.toLowerCase()) {
                    if (i < 3) {
                        return (Object.keys(COLOUR_INDEXES)[i]).toUpperCase();
                    }
                    return `COLOR${i + 1}`;
                }
            }
        }
    }
    // If we get here, the colour is not a valid campaign colour
    return undefined;
}

export const IS_COLOUR_NAME = (colour) => {
    if (COLOUR_INDEXES[colour?.toLowerCase()] !== undefined) {
        return true;
    }
    if (colour?.toLowerCase().startsWith('color') && !isNaN(colour.slice(5))) {
        return true;
    }
    return false;
}

export const GET_COLOUR_INDEX_FROM_COLOUR_NAME = (colour) => {
    if (COLOUR_INDEXES[colour?.toLowerCase()] !== undefined) {
        return COLOUR_INDEXES[colour?.toLowerCase()];
    }
    if (colour?.toLowerCase().startsWith('color') && !isNaN(colour.slice(5))) {
        const index = parseInt(colour.slice(5));
        return index - 1;
    }
    return undefined;
}

export const PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS = (customisationVars, featureData, campaignData) => {
    console.log('PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS: ', customisationVars, featureData, campaignData);
    
    // const customisationVars = JSON.parse(JSON.stringify(customisationVars_arg));
    const customisationData = featureData.feature.customisation;
    const key = featureData._id;
    console.log('Feature key: ', key);
    for (let i = 0; i < customisationData.length; i++) {
        const rootVal = customisationVars;
        const tabKey = customisationData[i].tabKey;
        for (let j = 0; j < customisationData[i].options.length; j++) {
            const varKey = customisationData[i].options[j].varKey;
            for (let k = 0; k < customisationData[i].options[j].variables.length; k++) {
                const varName = customisationData[i].options[j].variables[k].varName;
                for (let prop in customisationData[i].options[j].variables[k].valSchema) {
                    const type = customisationData[i].options[j].variables[k].valSchema[prop].type.toLowerCase();
                    if (type === 'colour') {
                        console.log('--- COLOUR FOUND: ', rootVal, tabKey, varKey, varName, prop);
                        const vals = getValFromObject(rootVal[tabKey], varKey, varName);
                        for (let l = 0; l < vals.length; l++) {
                            const currentColour = vals[l][prop];
                            console.log('>> Current Colour: ', currentColour);
                            vals[l][prop] = COLOUR_NAME_TO_COLOUR_HEX(currentColour, campaignData);
                            console.log('>> New Colour: ', vals[l][prop]);
                        }
                    }
                }
            }
        }
    }

    return customisationVars;
}

export const UPDATE_RECENT_COLOURS = (colour, campaignData) => {
    const recentColours = campaignData?.recentColours || [];
    const colourHex = COLOUR_NAME_TO_COLOUR_HEX(colour, campaignData);

    // If the colour already exists in the recent colours, move it to the start of the array
    const index = recentColours.findIndex((c) => c.toLowerCase() === colourHex.toLowerCase());
    if (index !== -1) {
        recentColours.splice(index, 1);
    }
    // Add the colour to the start of the array
    recentColours.unshift(colourHex);

    // Limit the array to 10 colours
    if (recentColours.length > 10) {
        recentColours.splice(10, recentColours.length - 10);
    }

    // Update the campaign data with the new recent colours
    campaignData.recentColours = recentColours;

    // Save the changes to the server - this happens purely in the background
    axios
        .post(updateCampaignRecentColoursRoute, {
            colours: recentColours,
            campaignID: campaignData._id,
            withCredentials:true
        })
        .then((res) => {
            if (res.data.success) {
                console.log('Recent colours updated successfully');
            } else {
                console.error('Error updating recent colours: ', res.data);
            }
        })
        .catch((err) => {
            console.error('Error updating recent colours: ', err);
        });
}



